import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {workspacePath} from '../services/paths.js';

const {FiAlertCircle, FiClock, FiFileText, FiArrowRight, FiCheckCircle} = FiIcons;

export default function HqAttentionStrip({state, usage}) {
  const items = [];

  // 1. Plan-limit alert
  if (usage.allowance > 0 && usage.used / usage.allowance >= 0.8) {
    items.push({
      id: 'plan_limit',
      severity: 'warning',
      icon: FiAlertCircle,
      title: `${Math.round((usage.used / usage.allowance) * 100)}% of AO allowance used`,
      description: `Only ${usage.remaining} Agent Operations remaining on ${usage.planName}.`,
      actionLabel: 'Upgrade plan',
      actionPath: workspacePath('settings', state)
    });
  }

  // 2. Overdue tasks
  const todayStr = new Date().toISOString().slice(0, 10);
  const overdueTasks = (state.tasks || []).filter(
    (t) => t.status !== 'done' && (t.due || t.due_date) && (t.due || t.due_date) < todayStr
  );
  if (overdueTasks.length > 0) {
    items.push({
      id: 'overdue_tasks',
      severity: 'danger',
      icon: FiClock,
      title: `${overdueTasks.length} overdue task${overdueTasks.length > 1 ? 's' : ''}`,
      description: `“${overdueTasks[0].title}” needs immediate follow-through.`,
      actionLabel: 'View tasks',
      actionPath: workspacePath('tasks', state)
    });
  }

  // 3. Pending approvals (plans in draft/review)
  const pendingPlans = (state.plans || []).filter((p) => p.status === 'draft' || p.status === 'review');
  if (pendingPlans.length > 0) {
    items.push({
      id: 'pending_plans',
      severity: 'info',
      icon: FiFileText,
      title: `${pendingPlans.length} plan${pendingPlans.length > 1 ? 's' : ''} awaiting approval`,
      description: `Latest: “${pendingPlans[0].title}” from ${state.bots?.find((b) => b.id === pendingPlans[0].botId)?.name || 'Lead'}.`,
      actionLabel: 'Review',
      actionPath: workspacePath('finance', state)
    });
  }

  // 4. Inbox items needing reply
  const unhandledInbox = (state.inbox || state.inboxMessages || []).filter(
    (m) => m.status === 'needs-reply' || m.triage_status === 'needs_review'
  );
  if (unhandledInbox.length > 0) {
    items.push({
      id: 'unhandled_inbox',
      severity: 'warning',
      icon: FiAlertCircle,
      title: `${unhandledInbox.length} incoming message${unhandledInbox.length > 1 ? 's' : ''} require reply`,
      description: `From ${unhandledInbox[0].from}: “${unhandledInbox[0].subject}”.`,
      actionLabel: 'Open inbox',
      actionPath: workspacePath('inbox', state)
    });
  }

  if (items.length === 0) {
    return (
      <div className="hq-attention-strip all-clear">
        <div className="attention-item-content">
          <SafeIcon icon={FiCheckCircle} className="icon-clear" />
          <div>
            <b>Attention: All clear</b>
            <p>No overdue tasks, plan limit alerts, or pending sign-offs need your attention.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hq-attention-strip" role="region" aria-label="Attention needed">
      <div className="attention-strip-head">
        <span className="eyebrow">Attention needed</span>
        <span className="count-pill">{items.length}</span>
      </div>
      <div className="attention-items-grid">
        {items.map((item) => (
          <div key={item.id} className={`attention-card ${item.severity}`}>
            <SafeIcon icon={item.icon} className="attention-icon" />
            <div className="attention-text">
              <b>{item.title}</b>
              <p>{item.description}</p>
            </div>
            <Link to={item.actionPath} className="button ghost compact attention-action">
              {item.actionLabel}
              <SafeIcon icon={FiArrowRight} />
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}
